import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useCorrection } from '../hooks/recipes/useCorrection';
import { buildDisplayItems, getProductionType, normalizeCorrection } from '../utils/recipes/recipeNormalization';

/**
 * RecipeDisplay context.
 *
 * Sits between raw Firestore data (production runs, production events, ingredients)
 * and the view layer. It owns all production-type-aware normalization so that the
 * recipe panel and the correction modals only render pre-structured, display-ready
 * data and never branch on production type or compute correction deltas themselves.
 *
 * Because the corrections modal is mounted both inside RecipeDisplay and inside
 * TopNavigation (siblings), this provider is placed high enough to cover both.
 */
const RecipeDisplayContext = createContext(null);

export function RecipeDisplayProvider({
  currentUser,
  shiftInfo,
  settings,
  productionRuns = [],
  productionEvents = [],
  ingredients = {},
  linkedDevices = [],
  fallbackUserLabel = 'User',
  children,
}) {
  // Drives "which run is currently running" resolution; refreshed each minute.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const {
    activeUserName,
    currentRunningProduction,
    newestActionablePendingMaterialCorrection,
    formatCorrectionDate,
    markCorrectionConfirmed,
  } = useCorrection({
    currentUser,
    shiftInfo,
    settings,
    productionRuns,
    productionEvents,
    now,
    fallbackUserLabel,
  });

  const productionType = useMemo(() => getProductionType(currentRunningProduction), [currentRunningProduction]);

  // Active conveyor, or null in all-devices mode — which links other devices instead of changing
  // `device_id`, so the linked list identifies the mode.
  const activeDeviceId = linkedDevices?.length > 0 ? null : currentUser?.device_id || null;

  // Uniform items for the live recipe panel, scoped to the active conveyor (correction applied
  // + corrected flags set).
  const displayItems = useMemo(() => {
    if (!currentRunningProduction) return [];
    return buildDisplayItems({
      production: currentRunningProduction,
      pendingCorrection: newestActionablePendingMaterialCorrection?.correction || null,
      ingredients,
      deviceId: activeDeviceId,
    });
  }, [currentRunningProduction, newestActionablePendingMaterialCorrection, ingredients, activeDeviceId]);

  // Normalize an arbitrary correction (e.g. the active modal correction) for display.
  // Defaults to the currently running production for type + recipe fallbacks.
  const normalizeCorrectionForDisplay = useCallback(
    (correction, production = currentRunningProduction) => normalizeCorrection(correction, production, ingredients),
    [currentRunningProduction, ingredients],
  );

  const value = useMemo(
    () => ({
      now,
      currentUser,
      shiftInfo,
      settings,
      ingredients,
      productionRuns,
      productionEvents,
      productionType,
      activeDeviceId,
      currentRunningProduction,
      displayItems,
      normalizeCorrection: normalizeCorrectionForDisplay,
      activeUserName,
      formatCorrectionDate,
      markCorrectionConfirmed,
      newestActionablePendingMaterialCorrection,
    }),
    [
      now,
      currentUser,
      shiftInfo,
      settings,
      ingredients,
      productionRuns,
      productionEvents,
      productionType,
      activeDeviceId,
      currentRunningProduction,
      displayItems,
      normalizeCorrectionForDisplay,
      activeUserName,
      formatCorrectionDate,
      markCorrectionConfirmed,
      newestActionablePendingMaterialCorrection,
    ],
  );

  return <RecipeDisplayContext.Provider value={value}>{children}</RecipeDisplayContext.Provider>;
}

/**
 * Access the RecipeDisplay context. Must be used within a RecipeDisplayProvider.
 */
export function useRecipeDisplay() {
  const context = useContext(RecipeDisplayContext);
  if (!context) {
    throw new Error('useRecipeDisplay must be used within a RecipeDisplayProvider');
  }
  return context;
}

export default RecipeDisplayContext;
